import { useCallback, useMemo } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import Decimal from 'decimal.js';
import { Transaction } from '@/api/rexel/ship-and-debit/useRexelAllTransactions';

const WORKBENCH_STORAGE_KEY = 'ship-and-debit-workbench';

export interface WorkbenchTransaction extends Transaction {
  addedAt: number;
  isModified: boolean;
  /** If set, this transaction failed to send */
  failureReason?: string;
  /** Timestamp when the transaction failed */
  failedAt?: number;
}

interface WorkbenchState {
  transactions: WorkbenchTransaction[];
}

export function useWorkbench() {
  const { value: state, set: setState } = useLocalStorageValue<WorkbenchState>(
    WORKBENCH_STORAGE_KEY,
    {
      defaultValue: { transactions: [] },
    },
  );

  const transactions = useMemo(
    () => state?.transactions ?? [],
    [state?.transactions],
  );

  const workbenchIds = useMemo(
    () => new Set(transactions.map((t) => t.id)),
    [transactions],
  );

  const addToWorkbench = useCallback(
    (newTransactions: Transaction | Transaction[]) => {
      const toAdd = Array.isArray(newTransactions)
        ? newTransactions
        : [newTransactions];

      setState((prev) => {
        const existing = prev?.transactions ?? [];
        const existingIds = new Set(existing.map((t) => t.id));

        const uniqueNew = toAdd
          .filter((t) => !existingIds.has(t.id))
          .map((t) => ({
            ...t,
            addedAt: Date.now(),
            isModified: false,
            failureReason: undefined,
            failedAt: undefined,
          }));

        return {
          transactions: [...existing, ...uniqueNew],
        };
      });
    },
    [setState],
  );

  const removeFromWorkbench = useCallback(
    (transactionIds: string | string[]) => {
      const idsToRemove = Array.isArray(transactionIds)
        ? new Set(transactionIds)
        : new Set([transactionIds]);

      setState((prev) => ({
        transactions: (prev?.transactions ?? []).filter(
          (t) => !idsToRemove.has(t.id),
        ),
      }));
    },
    [setState],
  );

  const updateTransaction = useCallback(
    (transactionId: string, updates: Partial<Transaction>) => {
      setState((prev) => ({
        transactions: (prev?.transactions ?? []).map((t) =>
          t.id === transactionId ? { ...t, ...updates, isModified: true } : t,
        ),
      }));
    },
    [setState],
  );

  /**
   * Mark transactions as failed with a reason
   * Used when external service rejects transactions
   */
  const markTransactionsAsFailed = useCallback(
    (failures: Array<{ id: string; reason: string }>) => {
      const failureMap = new Map(failures.map((f) => [f.id, f.reason]));

      setState((prev) => ({
        transactions: (prev?.transactions ?? []).map((t) => {
          const reason = failureMap.get(t.id);
          if (reason) {
            return {
              ...t,
              failureReason: reason,
              failedAt: Date.now(),
            };
          }
          return t;
        }),
      }));
    },
    [setState],
  );

  /**
   * Clear failure status from transactions (e.g., before retry)
   */
  const clearFailureStatus = useCallback(
    (transactionIds?: string | string[]) => {
      setState((prev) => ({
        transactions: (prev?.transactions ?? []).map((t) => {
          // If no IDs specified, clear all
          if (!transactionIds) {
            return { ...t, failureReason: undefined, failedAt: undefined };
          }

          const idsToClear = Array.isArray(transactionIds)
            ? new Set(transactionIds)
            : new Set([transactionIds]);

          if (idsToClear.has(t.id)) {
            return { ...t, failureReason: undefined, failedAt: undefined };
          }
          return t;
        }),
      }));
    },
    [setState],
  );

  const clearWorkbench = useCallback(() => {
    setState({ transactions: [] });
  }, [setState]);

  const getTransaction = useCallback(
    (transactionId: string) => {
      return state?.transactions?.find((t) => t.id === transactionId);
    },
    [state?.transactions],
  );

  const isInWorkbench = useCallback(
    (transactionId: string) => {
      return workbenchIds.has(transactionId);
    },
    [workbenchIds],
  );

  const totalClaimAmount = useMemo(
    () =>
      transactions
        .reduce((sum, t) => {
          const amount = new Decimal(t.claimAmount ?? 0);
          return sum.plus(amount);
        }, new Decimal(0))
        .toNumber(),
    [transactions],
  );

  const transactionCount = transactions.length;

  /**
   * Count of transactions that have failed
   */
  const failedCount = useMemo(
    () => transactions.filter((t) => t.failureReason).length,
    [transactions],
  );

  /**
   * Get all failed transactions
   */
  const failedTransactions = useMemo(
    () => transactions.filter((t) => t.failureReason),
    [transactions],
  );

  return {
    transactions,
    workbenchIds,
    addToWorkbench,
    removeFromWorkbench,
    updateTransaction,
    markTransactionsAsFailed,
    clearFailureStatus,
    clearWorkbench,
    getTransaction,
    isInWorkbench,
    totalClaimAmount,
    transactionCount,
    failedCount,
    failedTransactions,
  };
}
